import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { X } from 'lucide-react'
import { useTaskDetailQuery, useUpdateTaskMutation } from '../../api/tasks.js'
import { TASK_STATUSES } from '../../lib/board.js'
import { boardViewPath } from '../../lib/board-routes.js'
import { isTypingTarget } from '../../lib/keyboard.js'
import { TaskTitleField } from './TaskTitleField.js'
import { TaskDescriptionField } from './TaskDescriptionField.js'
import { TaskFieldsSidebar } from './TaskFieldsSidebar.js'
import { TaskParentLink, TaskSubtasks } from './TaskHierarchy.js'
import { TaskActivityThread } from './TaskActivityThread.js'

/**
 * Panel deslizante sobre el tablero, con ruta propia `/projects/:key/tasks/:id`
 * (CLAUDE.md §6). Se monta como hijo de `ProjectBoardPage` vía `<Outlet />`,
 * así que el tablero de debajo sigue montado y con su estado intacto (ver
 * docs/DECISIONS.md, fase 4).
 */
export function TaskDetailPanel() {
  // `status` solo existe si el panel está abierto sobre la vista de columna (fase 7).
  const { id, key = '', status } = useParams<{ id: string; key: string; status: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const taskId = id ?? ''
  const detail = useTaskDetailQuery(taskId)
  const update = useUpdateTaskMutation(taskId)

  function close(): void {
    void navigate({ pathname: boardViewPath(key, status), search: location.search })
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        close()
        return
      }
      // `1`/`2`/`3` mueven la tarea abierta de columna (CLAUDE.md §6). Con
      // guarda de campo de texto: el panel tiene título, descripción y
      // comentario editables, y "2" tecleado ahí no debe mover nada.
      if (isTypingTarget(event.target)) return
      const index = ['1', '2', '3'].indexOf(event.key)
      if (index === -1) return
      const status = TASK_STATUSES[index]
      if (status) update.mutate({ status })
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, status, taskId, location.search])

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={detail.data?.title ?? t('task.detail.loading')}
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-[720px] flex-col border-l border-border bg-surface shadow-lg"
    >
      <div className="flex shrink-0 items-center justify-end px-4 py-3">
        <button
          type="button"
          onClick={close}
          aria-label={t('common.close')}
          className="flex h-8 w-8 max-md:h-11 max-md:w-11 items-center justify-center rounded-card text-text-muted hover:bg-surface-hi hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {detail.isPending && <PanelSkeleton />}

      {detail.isError && (
        <p className="px-6 py-4 text-body text-danger">{t('task.detail.loadError')}</p>
      )}

      {detail.data && (
        // Móvil (CLAUDE.md §6, fase 6): columna derecha de campos apilada
        // debajo, no al lado — a 375px de ancho 224px de columna fija no
        // dejarían sitio para nada más.
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-hidden">
          <div className="flex min-w-0 flex-1 flex-col gap-4 px-6 pb-8 md:overflow-y-auto">
            {detail.data.parentId && <TaskParentLink parentId={detail.data.parentId} />}
            <TaskTitleField
              title={detail.data.title}
              onSave={(title) => update.mutate({ title })}
            />
            <TaskDescriptionField
              description={detail.data.description}
              onSave={(description) => update.mutate({ description })}
            />
            <TaskSubtasks task={detail.data} />
            <TaskActivityThread task={detail.data} />
          </div>

          <div className="shrink-0 border-t border-border px-4 py-4 md:w-56 md:overflow-y-auto md:border-l md:border-t-0 md:py-1">
            <TaskFieldsSidebar task={detail.data} />
          </div>
        </div>
      )}
    </div>
  )
}

function PanelSkeleton() {
  return (
    <div className="flex flex-col gap-4 px-6 pb-8" aria-hidden="true">
      <div className="h-7 w-2/3 animate-pulse rounded-card bg-surface-hi" />
      <div className="h-24 animate-pulse rounded-card bg-surface-hi" />
      <div className="h-32 animate-pulse rounded-card bg-surface-hi" />
    </div>
  )
}
